// @architeia/types/atlas — Conjuring Atlas: catalog and curation contracts.
// ============================================================================
// ATLAS CATALOG (ATLAS-7)
// ============================================================================
//
// Public read model for the Conjuring Atlas catalog API (`services/atlas`). These DTOs are the
// allow-list projection of `CatalogEntry`/`CatalogRelation` — never a spread of the Prisma row or
// stored JSON. Shared here (not service-local) because Secret Org's `AtlasTrickEmbed`
// (ENGAGEMENT_LAYER.md) will consume the same shapes. `status`, `fixtureKey` and the timestamps
// are Atlas-internal and never appear here (ATLAS-5 D4, ATLAS-7 D3).
// AINFRA-176 / ATLAS-36: this module is the one owner of the Atlas vocabularies and limits. The
// service (`services/atlas/src/domain`), the member UI, the admin panel and the API client import
// them; nobody re-declares a value. `services/atlas/tests/domain/sharedLimits.test.ts` fails if a
// literal equal to one of these limits reappears under `services/*/src/domain`.
export const ATLAS_CATALOG_ENTRY_TYPES = [
    'trick',
    'effect',
    'technique',
    'sleight',
    'principle',
    'routine'
];
export const ATLAS_CATALOG_DIFFICULTIES = [
    'beginner',
    'intermediate',
    'advanced',
    'expert'
];
/** Tamara `related` keys admitted to the catalog tier (ATLAS-5 D6). */
export const ATLAS_CATALOG_RELATION_TYPES = ['variation_of', 'inspired_by', 'similar_to'];
/** Tamara `id`: an optional namespace prefix, then a lowercase-kebab slug body, 2..100 chars total. */
export const ATLAS_SLUG_LIMITS = { min: 2, max: 100 };
export const ATLAS_SLUG_PATTERN = /^([a-z0-9-]+:)?[a-z0-9-]+$/;
/** Public catalog list query bounds (`GET /api/v1/catalog/tricks`, and the MCP search tool). */
export const ATLAS_CATALOG_QUERY_LIMITS = {
    queryMaxLength: 100,
    maxTagFilters: 10,
    minPage: 1,
    maxPage: 1000
};
/** ADR-007 rule 5 — relations stay within a tier. The service uses this table as its authority;
 * the member UI uses it for UX guards only. */
export const ATLAS_CATALOG_ENTRY_TIER = {
    trick: 'effect',
    effect: 'effect',
    routine: 'effect',
    technique: 'technique',
    sleight: 'technique',
    principle: 'technique'
};
// ============================================================================
// ATLAS CURATION (ATLAS-15)
// ============================================================================
//
// Authenticated curation API contract (`services/atlas` /api/v1/curation). NOT public catalog
// DTOs: status/revision/fixture/proposer are legitimate here, and the `AtlasCuration*` prefix keeps
// these from ever being reused for a public response. Views are allow-list projections built key
// by key in `services/atlas/src/services/curationReadModel.ts`. Optional props are
// `?: T | undefined` because `services/atlas` compiles with `exactOptionalPropertyTypes` and
// assigns zod-inferred values into these shapes. The credit and request shapes are not declared here:
// they are `z.infer` / `z.input` of `./atlas-schemas.ts` and re-exported type-only below, so the
// service validates with the very schemas the types come from (AINFRA-176).
/** Atlas-internal lifecycle; `archived` covers both a declined proposal and a retired entry. */
export const ATLAS_CURATION_STATUSES = ['draft', 'published', 'archived'];
export const ATLAS_CURATION_EVENT_ACTIONS = [
    'proposed',
    'edited',
    'published',
    'rejected',
    'unpublished',
    'reopened'
];
export const ATLAS_CURATION_NOTE_MAX_LENGTH = 1000;
/** Request body cap on the curation write routes. */
export const ATLAS_CURATION_REQUEST_BODY_MAX_BYTES = 65_536;
/** Page bound on the curation list routes (`proposals/mine`, `queue`, `events`). */
export const ATLAS_CURATION_LIST_MAX_PAGE = 1000;
/**
 * The Atlas curation HTTP surface (ATLAS-21): one row per route under `/api/v1/curation`. `path` is
 * the Fastify route template (`:uuid` is the entry id); `operation` is the `AtlasCurationClient`
 * method that calls it. Neither side may import the other, so both are tested against this list:
 * `services/atlas` must register exactly these routes, and `libs/api-client` must request exactly
 * these. A path that drifts on either side fails a test instead of 404-ing in the admin panel.
 */
export const ATLAS_CURATION_ROUTES = [
    { operation: 'createProposal', method: 'POST', path: '/api/v1/curation/proposals' },
    { operation: 'listMyProposals', method: 'GET', path: '/api/v1/curation/proposals/mine' },
    { operation: 'getEntry', method: 'GET', path: '/api/v1/curation/entries/:uuid' },
    { operation: 'editEntry', method: 'PUT', path: '/api/v1/curation/entries/:uuid' },
    { operation: 'listQueue', method: 'GET', path: '/api/v1/curation/queue' },
    { operation: 'listEvents', method: 'GET', path: '/api/v1/curation/entries/:uuid/events' },
    { operation: 'publishEntry', method: 'POST', path: '/api/v1/curation/entries/:uuid/publish' },
    { operation: 'rejectEntry', method: 'POST', path: '/api/v1/curation/entries/:uuid/reject' },
    { operation: 'unpublishEntry', method: 'POST', path: '/api/v1/curation/entries/:uuid/unpublish' },
    { operation: 'reopenEntry', method: 'POST', path: '/api/v1/curation/entries/:uuid/reopen' }
];
/** Length limits at the curation write boundary (proposals/edits). The service enforces them; the
 * frontend uses them for inline validation only (UX — the server is authoritative). */
export const ATLAS_CURATION_PROPOSAL_LIMITS = {
    titleMax: 200,
    descriptionMax: 4000,
    listItemsMax: 50,
    listItemLengthMax: 64,
    aliasesMax: 20,
    aliasLengthMax: 200
};
export const ATLAS_CURATION_RELATIONS_MAX = 50;
export const ATLAS_CURATION_RELATION_VIOLATION_CODES = [
    'self-edge',
    'target-not-found',
    'target-archived',
    'untyped-endpoint',
    'tier-mismatch',
    'variation-of-cycle',
    'variation-depth-exceeded',
    'multiple-variation-of',
    'similar-to-both-orientations'
];
